import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useIntegrity } from "../api/queries";
import type { FlaggedSubmission } from "../api/schemas";
import { SortHeader, tableStyles } from "../components/table";
import {
  Button,
  Chip,
  EmptyState,
  ErrorState,
  Field,
  PageHeader,
  Segmented,
  Skeleton,
  Stat,
  TextInput,
} from "../components/ui";
import { addDays, formatLongDate, todayUtc } from "../lib/dates";
import { fmtMs } from "../lib/format";
import styles from "./pages.module.css";

type FlagFilter = "all" | "too_fast" | "clock_mismatch" | "both";

const skew = (f: FlaggedSubmission) => (f.clientElapsedMs === null ? null : f.elapsedMs - f.clientElapsedMs);

export function IntegrityPage() {
  const today = todayUtc();
  const [params, setParams] = useSearchParams();
  const date = params.get("date") || today;
  const q = useIntegrity(date);
  const [flag, setFlag] = useState<FlagFilter>("all");
  const [user, setUser] = useState("");
  const [minScore, setMinScore] = useState("");
  const [sort, setSort] = useState("elapsed");

  const setDate = (d: string) => setParams(d && d !== today ? { date: d } : {}, { replace: true });

  const rows = useMemo(() => {
    const items = q.data?.items ?? [];
    const min = Number(minScore) || 0;
    const filtered = items.filter(
      (f) =>
        (flag === "all" ||
          (flag === "both" ? f.flags.length > 1 : f.flags.includes(flag))) &&
        (!user || f.userId.startsWith(user.trim().toLowerCase())) &&
        f.score >= min,
    );
    const key = sort.replace(/^-/, "");
    const dir = sort.startsWith("-") ? -1 : 1;
    const val = (f: FlaggedSubmission) => (key === "score" ? f.score : key === "skew" ? Math.abs(skew(f) ?? 0) : key === "user" ? f.userId : f.elapsedMs);
    return [...filtered].sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0) * dir);
  }, [q.data, flag, user, minScore, sort]);

  const items = q.data?.items ?? [];
  const tooFast = items.filter((f) => f.flags.includes("too_fast")).length;
  const clock = items.filter((f) => f.flags.includes("clock_mismatch")).length;

  return (
    <>
      <PageHeader
        title="Integrity"
        meta={<span>Submissions flagged by the anti-cheat heuristics for {formatLongDate(date)}.</span>}
        actions={
          <>
            <Button size="sm" variant="ghost" onClick={() => setDate(addDays(date, -1))} aria-label="Previous day">
              ←
            </Button>
            <TextInput type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} aria-label="Date" />
            <Button size="sm" variant="ghost" onClick={() => setDate(addDays(date, 1))} disabled={date >= today} aria-label="Next day">
              →
            </Button>
          </>
        }
      />

      <div className={styles.statStrip}>
        <Stat label="Flagged" value={q.data ? items.length : "—"} />
        <Stat label="Too fast" value={q.data ? tooFast : "—"} sub="server time under the floor" />
        <Stat label="Clock mismatch" value={q.data ? clock : "—"} sub="client vs server elapsed" />
        <Stat label="Scored > 0" value={q.data ? items.filter((f) => f.score > 0).length : "—"} sub="affect percentiles" />
      </div>

      <div className={styles.filters}>
        <Field label="Flag">
          <Segmented<FlagFilter>
            label="Flag filter"
            value={flag}
            onChange={setFlag}
            options={[
              { value: "all", label: "All" },
              { value: "too_fast", label: "Too fast" },
              { value: "clock_mismatch", label: "Clock" },
              { value: "both", label: "Both" },
            ]}
          />
        </Field>
        <Field label="User id prefix">
          <TextInput data-search value={user} onChange={(e) => setUser(e.target.value)} placeholder="3f9a…   ( / )" style={{ width: 200 }} />
        </Field>
        <Field label="Min score">
          <TextInput type="number" min={0} max={1000} step={50} value={minScore} onChange={(e) => setMinScore(e.target.value)} placeholder="0" style={{ width: 100 }} />
        </Field>
      </div>

      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <div className={tableStyles.wrap} aria-busy={q.isFetching}>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <SortHeader field="user" sort={sort} onSort={setSort}>
                  User
                </SortHeader>
                <th data-align="right">Puzzle</th>
                <th>Flags</th>
                <SortHeader field="elapsed" sort={sort} onSort={setSort} align="right">
                  Server time
                </SortHeader>
                <th data-align="right">Client time</th>
                <SortHeader field="skew" sort={sort} onSort={setSort} align="right">
                  Skew
                </SortHeader>
                <SortHeader field="score" sort={sort} onSort={setSort} align="right">
                  Score
                </SortHeader>
              </tr>
            </thead>
            <tbody>
              {q.isPending
                ? Array.from({ length: 5 }, (_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 7 }, (__, j) => (
                        <td key={j}>
                          <Skeleton height={12} width={j === 0 ? 220 : 60} />
                        </td>
                      ))}
                    </tr>
                  ))
                : rows.map((f) => {
                    const s = skew(f);
                    return (
                      <tr key={`${f.userId}-${f.puzzleNumber}`}>
                        <td>
                          <Link to={`/players/${f.userId}`} className={tableStyles.id}>
                            {f.userId}
                          </Link>
                        </td>
                        <td data-align="right" className={tableStyles.mono}>
                          #{f.puzzleNumber}
                        </td>
                        <td>
                          <span className={styles.chips}>
                            {f.flags.map((x) => (
                              <Chip key={x} tone={x === "too_fast" ? "danger" : "warning"}>
                                {x.replace("_", " ")}
                              </Chip>
                            ))}
                          </span>
                        </td>
                        <td data-align="right" className="mono">
                          {fmtMs(f.elapsedMs)}
                        </td>
                        <td data-align="right" className="mono">
                          {fmtMs(f.clientElapsedMs)}
                        </td>
                        <td data-align="right" className="mono" data-warn={s !== null && Math.abs(s) > 5000 ? true : undefined}>
                          {s === null ? "—" : `${s > 0 ? "+" : ""}${(s / 1000).toFixed(1)}s`}
                        </td>
                        <td data-align="right" className="mono">
                          {f.score}
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
          {!q.isPending && rows.length === 0 ? (
            <EmptyState title={items.length ? "Nothing matches these filters" : "No flagged submissions"}>
              {items.length ? "Loosen the filters to see all flags for this day." : "Either a clean day, or the puzzle isn't live yet."}
            </EmptyState>
          ) : null}
        </div>
      )}
    </>
  );
}
